import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from '@/features/admin/AdminError';

const TABS = [
  ['waiting', 'Waiting'],
  ['approved', 'Upheld'],
  ['declined', 'Declined'],
  ['all', 'All'],
] as const;

/**
 * The clinical desk.
 *
 * ── THE PROMISE THIS SCREEN KEEPS ───────────────────────────────────────────
 *
 * A buyer who reports that a medicine made them ill is told, in writing:
 *
 *     "Thank you for telling us. A clinician will read your report within 48
 *      hours and we will message you with what happens next."
 *
 * That sentence has been going out since refunds shipped. There was no screen
 * behind it — the reports landed in the admin refunds queue among four hundred
 * late deliveries and damaged boxes, and were read, if at all, by operations.
 * The `pharmacist` role built for exactly this job could not sign in anywhere.
 *
 * ── WHY THE QUEUE IS OLDEST FIRST ───────────────────────────────────────────
 *
 * Every other queue in this product is newest first. This one is not, because
 * the promise is a 48-hour reading and a newest-first queue puts the report
 * closest to breaking that promise hardest to find.
 *
 * ── AND WHY A DECLINE DEMANDS TWENTY CHARACTERS ─────────────────────────────
 *
 * Whatever is written is sent to the person who reported it. "Declined" is not
 * an answer to somebody who has just said a medicine hurt them, and a reviewer
 * who cannot spend a sentence on it should not be deciding it.
 */
export function ClinicalQueueScreen() {
  const [status, setStatus] = useState<string>('waiting');
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['clinical', 'reports', status],
    queryFn: ({ signal }) => api.clinicalReports(status, signal),
    refetchInterval: 120_000,
  });

  if (isError) {
    return <AdminError title="We couldn't load the reports" error={error} onRetry={() => refetch()} />;
  }

  const reports = data?.reports ?? [];

  return (
    <div className="mx-auto w-full max-w-[900px] px-4 py-6 xl:px-8">
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">
          Reaction reports
        </h1>
        <p className="m-0 mt-1 max-w-[62ch] text-[13px] leading-relaxed text-muted">
          Buyers who say a medicine made them ill. Each was promised a reading within 48 hours, and
          whatever you write is sent to them.
        </p>
      </header>

      {data ? (
        <div className="mb-4 grid grid-cols-2 gap-2.5 sm:max-w-[400px]">
          <Stat label="Waiting for you" value={data.waiting} />
          {/* The promise, broken. Amber rather than a date to subtract. */}
          <Stat label="Past 48 hours" value={data.overdue} warn={data.overdue > 0} />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setStatus(key)}
            className={cn(
              'min-h-11 rounded-btn border px-3.5 text-[13px] font-medium',
              status === key
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2.5">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-[92px] w-full" radius="card" />
          ))}
        </div>
      ) : reports.length === 0 ? (
        <EmptyState
          title={status === 'waiting' ? 'Nothing waiting' : 'Nothing here'}
          body="When somebody reports a reaction to a medicine, it appears here with what they wrote."
          action={
            <Button variant="grey" onClick={() => setStatus('all')}>
              Show everything
            </Button>
          }
        />
      ) : (
        <div className="space-y-2.5">
          {reports.map((report) => (
            <Row
              key={report.reference}
              report={report}
              open={open === report.reference}
              onToggle={() => setOpen(open === report.reference ? null : report.reference)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, warn = false }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className={cn('rounded-card border bg-white px-3.5 py-3', warn ? 'border-caution' : 'border-line')}>
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">{label}</p>
      <p
        className={cn(
          'm-0 mt-0.5 font-display text-[22px] font-bold tracking-[-0.02em] tabular',
          warn ? 'text-caution-ink' : 'text-ink',
        )}
      >
        {value}
      </p>
    </div>
  );
}

function Row({
  report,
  open,
  onToggle,
}: {
  report: import('@chicare/shared').ClinicalReportRow;
  open: boolean;
  onToggle: () => void;
}) {
  const waiting = report.status === 'open' || report.status === 'in_review';

  return (
    <div
      className={cn(
        'overflow-hidden rounded-card border bg-white',
        report.is_overdue ? 'border-caution' : 'border-line',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full min-h-11 items-start gap-3 px-4 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              {report.reference}
            </span>
            {report.is_overdue ? (
              <span className="rounded-pill bg-caution-tint px-2 py-[1px] font-mono text-[9.5px] uppercase tracking-[0.1em] text-caution-ink">
                {report.hours_waiting}h waiting
              </span>
            ) : null}
            {!waiting ? (
              <span className="rounded-pill bg-paper px-2 py-[1px] font-mono text-[9.5px] uppercase tracking-[0.1em] text-muted">
                {report.status === 'approved' ? 'Upheld' : 'Declined'}
              </span>
            ) : null}
          </span>

          <span className="mt-0.5 block text-[13.5px] font-semibold text-ink">{report.buyer}</span>
          <span className="mt-0.5 block text-[11.5px] text-faint">
            {[report.pharmacy, report.order_reference].filter(Boolean).join(' · ')}
          </span>
        </span>

        <Icon
          name={open ? 'up' : 'down'}
          size={16}
          strokeWidth={2}
          className="mt-1 shrink-0 text-muted"
        />
      </button>

      {open ? <Detail reference={report.reference} decidable={waiting} /> : null}
    </div>
  );
}

/**
 * What they wrote, what they were sent, and the decision.
 *
 * Loaded only when opened. The description is the clinical evidence and it is
 * the one thing a reviewer cannot decide without — but it is also somebody
 * describing their own body, so it is fetched when a clinician deliberately
 * opens one report rather than printed down a list of forty.
 */
function Detail({ reference, decidable }: { reference: string; decidable: boolean }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['clinical', 'report', reference],
    queryFn: ({ signal }) => api.clinicalReport(reference, signal),
  });

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['clinical'] });
    setNote('');
  };

  const approve = useMutation({
    mutationFn: () => api.approveClinicalReport(reference, note.trim() || undefined),
    onSuccess: done,
  });

  const decline = useMutation({
    mutationFn: () => api.declineClinicalReport(reference, note.trim()),
    onSuccess: done,
  });

  const busy = approve.isPending || decline.isPending;
  const error = [approve.error, decline.error].find((e): e is ApiError => e instanceof ApiError);

  if (isLoading || !data) {
    return (
      <div className="space-y-2 border-t border-line p-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="border-t border-line bg-paper p-4">
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        What they told us
      </p>
      <p className="m-0 mt-1.5 whitespace-pre-line text-[13.5px] leading-relaxed text-ink">
        {data.description || 'They did not write anything.'}
      </p>

      {data.items.length > 0 ? (
        <>
          <p className="m-0 mt-4 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            What they were sent
          </p>
          <ul className="m-0 mt-1.5 list-none space-y-1 p-0">
            {data.items.map((item, i) => (
              <li key={i} className="text-[12.5px] text-ink">
                {item.name}
                {item.batch_no ? (
                  // The batch is the clinical thread: it is what links this
                  // report to anybody else's.
                  <span className="ml-1.5 font-mono text-[11.5px] text-muted">
                    batch {item.batch_no}
                    {item.expiry_date ? ` · exp ${item.expiry_date}` : ''}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {data.photo_count > 0 ? (
        <>
          <p className="m-0 mt-4 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            {data.photo_count} photo{data.photo_count === 1 ? '' : 's'}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {Array.from({ length: data.photo_count }, (_, i) => (
              <img
                key={i}
                src={api.clinicalPhotoUrl(reference, i)}
                alt={`Photograph ${i + 1} of the reaction`}
                className="h-24 w-24 rounded-card border border-line object-cover"
              />
            ))}
          </div>
        </>
      ) : null}

      {/* One report is an incident; three on one batch is a recall
          conversation, and a reviewer deciding the third without knowing
          about the first two is deciding blind. */}
      {data.signal?.clustered ? (
        <p className="m-0 mt-4 flex items-start gap-2 rounded-card border border-caution bg-caution-tint px-3 py-2.5 text-[12.5px] leading-snug text-caution-ink">
          <Icon name="alert" size={14} strokeWidth={2} className="mt-px shrink-0" />
          <span>
            Other buyers have reported the same batch. This is not an isolated report.
          </span>
        </p>
      ) : null}

      {decidable ? (
        <div className="mt-4 border-t border-line pt-4">
          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
              What should they be told?
            </span>
            <textarea
              value={note}
              rows={3}
              onChange={(event) => setNote(event.target.value)}
              placeholder="They read this. Say what you concluded and what they should do now."
              className="w-full rounded-inp border border-line bg-white px-3 py-2 text-[13.5px] leading-relaxed text-ink outline-none focus:border-blue"
            />
          </label>

          {error ? (
            <p role="alert" className="m-0 mt-2 text-[12.5px] font-medium text-danger">
              {error.fieldErrors.note?.[0] ?? error.message}
            </p>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" disabled={busy} onClick={() => approve.mutate()}>
              {approve.isPending ? 'Upholding…' : 'Uphold and refund'}
            </Button>
            <Button size="sm" variant="grey" disabled={busy} onClick={() => decline.mutate()}>
              {decline.isPending ? 'Declining…' : 'Decline'}
            </Button>
          </div>

          <p className="m-0 mt-2 text-[11.5px] leading-snug text-faint">
            A decline needs a real answer — at least a sentence. It goes to them, and it is
            recorded against your name.
          </p>
        </div>
      ) : data.decision_note ? (
        <div className="mt-4 border-t border-line pt-3">
          <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            What they were told
          </p>
          <p className="m-0 mt-1 whitespace-pre-line text-[12.5px] leading-relaxed text-ink">
            {data.decision_note}
          </p>
        </div>
      ) : null}
    </div>
  );
}
